// src/services/auth/AuthServiceImpl.ts

import { AuthService } from '@/services/abstract/AuthService';
import { createLogger } from '@/utils/logger';
import type {
  BeginRegistrationDTO,
  VerificationDTO,
  FinalizeLoginDTO,
  FinalizeVerifyDTO,
  InitLoginDTO,
  InitRegistrationDTO,
  LoginDTO,
  LoginResult,
  RecoveryVerificationDTO,
} from '@/types/auth/AuthDTOs';
import type { FetcherService } from '@/services/abstract/FetcherService';
import { authEndpoints } from '@/api/endpoints';
import {
  createRegistrationRequest,
  performOpaqueRegistration,
} from '@/utils/opaque-registration.utils';
import { createAuthInit, performOpaqueAuth } from '@/utils/opaque-login.utils';
import type { FetchOptions } from '@/types/shared/api.types';

const log = createLogger({ namespace: 'six3ro:auth' });

/**
 * Implementation of authentication service handling user login, registration,
 * and two-factor authentication flows.
 *
 * Provides complete authentication flow including:
 * - Login/logout operations
 * - Multi-step registration wizard
 * - Two-factor authentication
 *
 * @see AuthService
 */
export class AuthServiceImpl extends AuthService {
  /**
   * Creates an instance of AuthServiceImpl
   * @param fetcher - Service for making HTTP requests to the backend
   */
  constructor(private readonly fetcher: FetcherService) {
    super();
  }

  // --- Login / Logout ---

  /**
   * Authenticates a user with credentials
   * @endpoint POST /auth/login
   * @param data - Login credentials and optional 2FA information
   * @request LoginDTO - { username: string; password: string; twoFactorCode?: string }
   * @returns LoginResult containing authentication token and user information, or 2FA requirement
   * @throws AuthenticationError if credentials are invalid
   * @throws ValidationError if input data is malformed
   * @tags auth, login
   */
  async login(data: LoginDTO): Promise<LoginResult> {
    const { username, password } = data;

    // Step 1: Create OPAQUE auth initialization (KE1) and get client instance
    const { ke1, client } = await createAuthInit(password);

    // Step 2: Send KE1 to server
    const serverResponse = await this.initLogin({ username, ke1 });

    // Step 3: Process server's KE2 and generate KE3 using same client instance
    const { sessionId, ke3 } = await performOpaqueAuth(
      client,
      username,
      serverResponse
    );

    // Step 4: Finalize login and verify session key
    const { maskedEmail, maskedPhone, twoFactorToken } = await this.finalizeLogin({ sessionId, ke3 });
    return { twoFactorToken, maskedEmail, maskedPhone };
  }

  async initLogin(data: { username: string; ke1: string }): Promise<InitLoginDTO> {
    return await this.fetcher.post(authEndpoints.initLogin, data);
  }

  async finalizeLogin(data: { sessionId: string; ke3: string }): Promise<FinalizeLoginDTO> {
    return await this.fetcher.post(authEndpoints.finalizeLogin, data);
  }

  /**
   * Logs out the current user. Token clearing is handled by logoutThunk.
   * This method is a placeholder for future server-side session invalidation.
   */
  // eslint-disable-next-line @typescript-eslint/class-methods-use-this -- placeholder for server-side invalidation
  async logout(): Promise<void> {
    await Promise.resolve();
  }

  // --- Registration wizard ---

  /**
   * Initiates the registration process for a new user
   * @endpoint POST /auth/begin-registration
   * @param data - Initial registration data
   * @request { username: string; otp: string }
   * @returns Object containing a registration token for subsequent steps
   * @throws ValidationError if username is already taken or OTP is invalid
   * @throws RateLimitError if too many registration attempts
   * @tags auth, registration
   */
  async beginRegistration(data: { username: string; otp: string }): Promise<BeginRegistrationDTO> {
    return await this.fetcher.post<BeginRegistrationDTO>(authEndpoints.validateOtp, data);
  }

  /**
   * Sets the password for a user during registration
   * @endpoint POST /auth/set-password
   * @param data - Registration token and desired password
   * @request { regToken: string; password: string }
   * @returns void
   * @throws ValidationError if password doesn't meet security requirements
   * @throws UnauthorizedError if registration token is invalid or expired
   * @tags auth, registration, password
   */
  async setPassword(data: { username: string; password: string }): Promise<void> {
    const { username, password } = data;
    const email = username;
    try {
      // Step 1: Create OPAQUE registration request and get client instance
      log.info('Starting registration', { username });
      const { registrationRequest, client } = await createRegistrationRequest(password);

      log.debug('Registration request created, sending to server');
      // Step 2: Send to server for OPRF evaluation
      const serverResponse = await this.initRegistration({
        username,
        registrationRequest,
      });
      log.debug('Server response received, finalizing registration');
      // Step 3: Finalize registration using same client instance and send record to server
      const { sessionId, registrationRecord } = await performOpaqueRegistration(
        client,
        serverResponse,
        email
      );

      await this.finalizeRegistration({ sessionId, registrationRecord });
    } finally {
      // SECURITY: Clear password from memory
      data.password = '';
    }
  }

  /**
   * Confirms the user's email address with a verification code
   * @endpoint POST /auth/confirm-email
   * @param data - Registration token and email verification code
   * @request { code: string }
   * @returns void
   * @throws ValidationError if verification code is incorrect
   * @throws UnauthorizedError if registration token is invalid or expired
   * @tags auth, registration, email, verification
   */
  async confirmEmail(data: { code: string }): Promise<void> {
    await this.fetcher.post(authEndpoints.verifyEmail, data);
  }

  /**
   * Requests a new email verification code to be sent
   * @endpoint POST /auth/resend-email-code
   * @param regToken - Registration token
   * @request { }
   * @returns void
   * @throws UnauthorizedError if registration token is invalid or expired
   * @throws RateLimitError if too many resend requests
   * @tags auth, registration, email, verification
   */
  async resendEmailCode(): Promise<VerificationDTO> {
    return await this.fetcher.post<VerificationDTO>(authEndpoints.resendEmailCode);
  }

  async sendEmailCode(): Promise<VerificationDTO> {
    return await this.fetcher.post(authEndpoints.sendEmailCode);
  }

  /**
   * Confirms the user's phone number with a verification code
   * @endpoint POST /auth/confirm-phone
   * @param data - Registration token and phone verification code
   * @request { regToken: string; code: string }
   * @returns void
   * @throws ValidationError if verification code is incorrect
   * @throws UnauthorizedError if registration token is invalid or expired
   * @tags auth, registration, phone, verification
   */
  async confirmPhone(data: { code: string }): Promise<void> {
    await this.fetcher.post(authEndpoints.verifyPhone, data);
  }

  /**
   * Requests a new phone verification code to be sent
   * @endpoint POST /auth/resend-phone-code
   * @param regToken - Registration token
   * @request { }
   * @returns void
   * @throws UnauthorizedError if registration token is invalid or expired
   * @throws RateLimitError if too many resend requests
   * @tags auth, registration, phone, verification
   */
  async resendPhoneCode(): Promise<VerificationDTO> {
    return await this.fetcher.post(authEndpoints.resendPhoneCode);
  }

  async sendPhoneCode(): Promise<VerificationDTO> {
    return await this.fetcher.post(authEndpoints.sendPhoneCode);
  }

  // --- Two-Factor ---

  /**
   * Selects the preferred two-factor authentication method for the session
   * @endpoint POST /private/v1/auth/2fa/choose-method
   * @param data - Session ID and selected 2FA method
   * @request { sessionToken: string; method: "sms" | "email" }
   * @returns void
   * @throws UnauthorizedError if session ID is invalid or expired
   * @throws ValidationError if selected method is not available for user
   * @tags auth, 2fa, two-factor
   */
  async selectTwoFactorMethod(data: {
    twoFactorToken: string;
    method: 'sms' | 'email';
  }): Promise<VerificationDTO> {
    return await this.fetcher.post(authEndpoints.chooseTwoFactorMethod, data);
  }

  /**
   * Verifies a two-factor authentication code and completes login
   * @endpoint POST /auth/verify-2fa
   * @param data - Session ID and 2FA verification code
   * @request { sessionToken: string; code: string }
   * @returns LoginResult containing authentication token and user information
   * @throws ValidationError if 2FA code is incorrect
   * @throws UnauthorizedError if session ID is invalid or expired
   * @tags auth, 2fa, two-factor, verification
   */
  async verifyTwoFactorCode(data: {
    code: string;
    twoFactorToken: string;
  }): Promise<FinalizeVerifyDTO> {
    return await this.fetcher.post(authEndpoints.verifyTwoFactor, data);
  }

  /**
   * Requests a new two-factor authentication code to be sent
   * @endpoint POST /auth/resend-2fa
   * @param data - Session ID
   * @request { sessionToken: string }
   * @returns void
   * @throws UnauthorizedError if session ID is invalid or expired
   * @throws RateLimitError if too many resend requests
   * @tags auth, 2fa, two-factor
   */
  async resendTwoFactorCode(data: {
    method: 'sms' | 'email';
    twoFactorToken: string;
  }): Promise<VerificationDTO> {
    return await this.fetcher.post(authEndpoints.resendTwoFactor);
  }

  // --- Password Recovery ---

  async forgotPassword(data: { emailAddress: string }): Promise<void> {
    await this.fetcher.post(authEndpoints.forgotPassword, data);
  }

  async verifyRecoveryOtp(data: { emailAddress: string; code: string }): Promise<RecoveryVerificationDTO> {
    return await this.fetcher.post<RecoveryVerificationDTO>(authEndpoints.verifyRecoveryOtp, data);
  }

  async sendRecoveryTwoFactor(data: { recoveryToken: string }): Promise<VerificationDTO> {
    return await this.fetcher.post<VerificationDTO>(authEndpoints.recoverySendTwoFactor, data);
  }

  async verifyRecoveryTwoFactor(data: { recoveryToken: string; code: string }): Promise<void> {
    await this.fetcher.post(authEndpoints.recoveryVerifyTwoFactor, data);
  }

  async initRegistration(data: {
    username: string;
    registrationRequest: string;
  }): Promise<InitRegistrationDTO> {
    return await this.fetcher.post(authEndpoints.initRegistration, data);
  }

  async finalizeRegistration(data: {
    sessionId: string;
    registrationRecord: string;
  }): Promise<void> {
    await this.fetcher.post(authEndpoints.finalizeRegistration, data);
  }

  async registerMlkemKeys(id: string, data: { encapsKey: string; decapsKey: string }, registrationToken?: string): Promise<void> {
    const options: FetchOptions & { onUploadProgress?: (event: ProgressEvent) => void } = registrationToken !== undefined && registrationToken !== '' ? {
      headers: { Authorization: `Bearer ${registrationToken}` }
    } : {};
    await this.fetcher.post(authEndpoints.registerMlkemKeys(id), data, options);
  }

  async updateMlkemKeys(id: string, data: { encapsKey: string; decapsKey: string }, token?: string): Promise<void> {
    const options: FetchOptions & { onUploadProgress?: (event: ProgressEvent) => void } = token !== undefined && token !== '' ? {
      headers: { Authorization: `Bearer ${token}` }
    } : {};
    await this.fetcher.patch(authEndpoints.updateMlkemKeys(id), data, options);
  }
}
